import { useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { inputClass, selectClass, labelClass } from '../../styles/class-utils'
import type { CreateOfferPayload } from '../../types'
import { ProductPicker } from '../ProductPicker'

export type OfferSubscriptionType = 'none' | 'autoship' | 'embedded' | 'standalone'

export interface OfferEditorValue extends CreateOfferPayload {
  variantId?: string
  imageUrl?: string
  subscriptionType?: OfferSubscriptionType
  subscriptionName?: string
  minStock?: number
  downsellOfferId?: string
}

interface OfferEditorProps {
  value: OfferEditorValue
  onChange: (value: OfferEditorValue) => void
}

const PRICING_OPTIONS: Array<{ value: 'fixed' | 'percentage' | 'free'; label: string }> = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'free', label: 'Free' },
]

const SHIPPING_OPTIONS: Array<{ value: 'free' | 'standard' | 'override'; label: string }> = [
  { value: 'free', label: 'Free' },
  { value: 'standard', label: 'Standard' },
  { value: 'override', label: 'Override' },
]

const SUBSCRIPTION_OPTIONS: Array<{ value: OfferSubscriptionType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'autoship', label: 'Autoship' },
  { value: 'embedded', label: 'Embedded' },
  { value: 'standalone', label: 'Standalone' },
]

function updateValue(
  value: OfferEditorValue,
  onChange: (value: OfferEditorValue) => void,
  patch: Partial<OfferEditorValue>,
) {
  onChange({ ...value, ...patch })
}

export function OfferEditor({ value, onChange }: OfferEditorProps) {
  const imagePreview = useMemo(() => value.imageUrl?.trim() ?? '', [value.imageUrl])

  return (
    <div className="space-y-4 rounded-lg border border-fn-border bg-tf-bg-hover p-4">
      <h3 className="text-sm font-semibold text-fn-text">Offer Editor</h3>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <ProductPicker
            value={value.productId ?? ''}
            mode="single"
            onChange={(nextValue) => updateValue(value, onChange, { productId: String(nextValue) })}
            label="Product"
          />
        </div>
        <div>
          <label className={labelClass}>Variant ID</label>
          <input
            type="text"
            className={inputClass}
            value={value.variantId ?? ''}
            onChange={(e) => updateValue(value, onChange, { variantId: e.target.value })}
            placeholder="var_..."
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Headline</label>
        <input
          type="text"
          className={inputClass}
          value={value.headline ?? ''}
          onChange={(e) => updateValue(value, onChange, { headline: e.target.value })}
          placeholder="Yes! Add this to your order"
        />
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea
          className={inputClass}
          rows={2}
          value={value.description ?? ''}
          onChange={(e) => updateValue(value, onChange, { description: e.target.value })}
          placeholder="Offer description"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>CTA Text</label>
          <input
            type="text"
            className={inputClass}
            value={value.ctaText ?? ''}
            onChange={(e) => updateValue(value, onChange, { ctaText: e.target.value })}
            placeholder="Yes! Add to my order"
          />
        </div>
        <div>
          <label className={labelClass}>Decline Text</label>
          <input
            type="text"
            className={inputClass}
            value={value.declineText ?? ''}
            onChange={(e) => updateValue(value, onChange, { declineText: e.target.value })}
            placeholder="No thanks"
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Image URL</label>
        <input
          type="url"
          className={inputClass}
          value={value.imageUrl ?? ''}
          onChange={(e) => updateValue(value, onChange, { imageUrl: e.target.value })}
          placeholder="https://..."
        />
        {imagePreview && (
          <img
            src={imagePreview}
            alt="Offer preview"
            className="mt-2 h-20 w-full rounded-md border border-fn-border object-contain bg-fn-card"
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Pricing Type</label>
          <div className="relative">
            <select
              className={selectClass}
              value={value.pricingType ?? 'fixed'}
              onChange={(e) => updateValue(value, onChange, { pricingType: e.target.value as 'fixed' | 'percentage' | 'free' })}
            >
              {PRICING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fn-text-muted" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Pricing Value</label>
          <input
            type="number"
            className={inputClass}
            value={value.pricingValue ?? 0}
            onChange={(e) => updateValue(value, onChange, { pricingValue: Number.parseFloat(e.target.value) || 0 })}
            min={0}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Shipping Type</label>
          <div className="relative">
            <select
              className={selectClass}
              value={value.shippingType ?? 'free'}
              onChange={(e) => updateValue(value, onChange, { shippingType: e.target.value as 'free' | 'standard' | 'override' })}
            >
              {SHIPPING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fn-text-muted" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Shipping Value</label>
          <input
            type="number"
            className={inputClass}
            value={value.shippingValue ?? 0}
            onChange={(e) => updateValue(value, onChange, { shippingValue: Number.parseFloat(e.target.value) || 0 })}
            min={0}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Subscription Type</label>
          <div className="relative">
            <select
              className={selectClass}
              value={value.subscriptionType ?? 'none'}
              onChange={(e) => updateValue(value, onChange, { subscriptionType: e.target.value as OfferSubscriptionType })}
            >
              {SUBSCRIPTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fn-text-muted" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Subscription Name</label>
          <input
            type="text"
            className={inputClass}
            value={value.subscriptionName ?? ''}
            onChange={(e) => updateValue(value, onChange, { subscriptionName: e.target.value })}
            placeholder="Monthly plan"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm text-fn-text">
          <input
            type="checkbox"
            checked={value.displayQuantity ?? false}
            onChange={(e) => updateValue(value, onChange, { displayQuantity: e.target.checked })}
          />
          Display Qty
        </label>
        <label className="flex items-center gap-2 text-sm text-fn-text">
          <input
            type="checkbox"
            checked={value.limitQuantity ?? false}
            onChange={(e) => updateValue(value, onChange, { limitQuantity: e.target.checked })}
          />
          Limit Qty
        </label>
        <div>
          <label className={labelClass}>Max Qty</label>
          <input
            type="number"
            className={inputClass}
            value={value.maxQuantity ?? 1}
            onChange={(e) => updateValue(value, onChange, { maxQuantity: Number.parseInt(e.target.value, 10) || 1 })}
            min={1}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Minimum Stock</label>
          <input
            type="number"
            className={inputClass}
            value={value.minStock ?? 0}
            onChange={(e) => updateValue(value, onChange, { minStock: Number.parseInt(e.target.value, 10) || 0 })}
            min={0}
          />
        </div>
        <div>
          <label className={labelClass}>Downsell Offer ID</label>
          <input
            type="text"
            className={inputClass}
            value={value.downsellOfferId ?? ''}
            onChange={(e) => updateValue(value, onChange, { downsellOfferId: e.target.value })}
            placeholder="Optional"
          />
        </div>
      </div>
    </div>
  )
}
